import { TextInput, Icon, CheckInput } from '@octano/global-ui';
import { Suspense, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../../components/text';

export const prefix = 'studyPlans.curriculum.degreeAverage';

interface DegreeAverageOptionsProps {
  readOnly: boolean;
  initialGraduationPercentage?: number;
}

export const DegreeAverageOptions = ({
  readOnly,
  initialGraduationPercentage,
}: DegreeAverageOptionsProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const [hasActivities, setHasActivities] = useState(false);

  const autoCalculate = useWatch({
    control,
    name: 'autoCalculate',
  });

  const graduationPercentage = useWatch({
    control,
    name: 'graduationPercentage',
  });

  const activitiesPercentage = useWatch({
    control,
    name: 'activitiesPercentage',
  });

  const curriculumActivities = useWatch({
    control,
    name: 'curriculumActivities',
  });

  useEffect(() => {
    if (!initialGraduationPercentage) return;
    setValue('autoCalculate', initialGraduationPercentage ?? true);
    setValue(
      'graduationPercentage',
      initialGraduationPercentage?.toString() || '',
    );
    setValue(
      'activitiesPercentage',
      (100 - (initialGraduationPercentage || 0)).toString(),
    );
  }, [initialGraduationPercentage, setValue, readOnly]);

  useEffect(() => {
    if (readOnly || !hasActivities) return;

    const graduation = Number(graduationPercentage) || 0;
    const activities = Number(activitiesPercentage) || 0;

    const lastChanged = document.activeElement?.getAttribute('name');

    if (lastChanged === 'graduationPercentage') {
      const newActivities = Math.max(0, 100 - graduation);
      if (newActivities !== activities) {
        setValue('activitiesPercentage', newActivities.toString());
      }
    } else if (lastChanged === 'activitiesPercentage') {
      const newGraduation = Math.max(0, 100 - activities);
      if (newGraduation !== graduation) {
        setValue('graduationPercentage', newGraduation.toString());
      }
    }
  }, [
    graduationPercentage,
    activitiesPercentage,
    setValue,
    readOnly,
    hasActivities,
  ]);

  useEffect(() => {
    if (readOnly) return;
    const activitiesNumber = curriculumActivities?.length || 0;
    if (activitiesNumber === 0) {
      setValue('graduationPercentage', '100');
      setValue('activitiesPercentage', '0');
    }
    setHasActivities(activitiesNumber > 0);
  }, [setValue, readOnly, curriculumActivities]);

  const boxStyle = {
    fontSize: '1rem',
    fontWeight: 'bold',
    padding: '0.5rem 1.5rem',
    border: '1px solid var(--primary)',
    color: 'var(--primary)',
    backgroundColor: 'var(--tertiary)',
    borderRadius: '0.5rem',
    minWidth: '210px',
    textAlign: 'center' as const,
  };

  const formatNumericValue = (value: any) => {
    const newValue = value.replace(/\D/g, '');
    return newValue;
  };

  const handlePercentageChange = (value: string) => {
    const formattedValue = formatNumericValue(value);
    return formattedValue;
  };

  return (
    <Suspense fallback={'loading...'}>
      <div className="legend-table pb-5">
        <Row>
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className="pt-3">
            <div style={{ fontWeight: 'bold' }}>
              <CheckInput
                name="autoCalculate"
                value={autoCalculate}
                label={t(`${prefix}.checkbox.label`)}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={() => {
                  setValue('autoCalculate', !autoCalculate);
                }}
              />
            </div>
            <div style={{ marginTop: '-20px', marginLeft: '28px' }}>
              {t(`${prefix}.checkbox.description`)}
            </div>
          </Col>
        </Row>
        {autoCalculate && (
          <>
            <Row className="py-4">
              <Col xs={12}>
                <div className="d-flex align-items-start">
                  <Icon name="information" size="20px" className="mt-1" />
                  <div>
                    <span className="fs-18 font-weight-bold pl-2">
                      {t(`${prefix}.information.bold`)}
                    </span>
                    <span className="fs-18">
                      {t(`${prefix}.information.text`)}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="pb-4">
              <Col xs={12}>
                <div
                  className="d-flex align-items-center"
                  style={{ paddingLeft: '26px' }}
                >
                  <div style={boxStyle}>{t(`${prefix}.degreeAverage`)}</div>
                  <span
                    className="mx-2 font-weight-bold"
                    style={{ color: 'var(--primary)' }}
                  >
                    =
                  </span>
                  <div style={boxStyle}>
                    {t(`${prefix}.graduationPercentageFormula`)}
                  </div>
                  <span
                    className="mx-2 font-weight-bold"
                    style={{ color: 'var(--primary)' }}
                  >
                    +
                  </span>
                  <div style={boxStyle}>{t(`${prefix}.activitiesFormula`)}</div>
                </div>
              </Col>
            </Row>
            <Row className="mb-4">
              <Col xs={12} md={6}>
                <TextInput
                  label={t(`${prefix}.graduationPercentage`)}
                  value={graduationPercentage}
                  name="graduationPercentage"
                  control={control}
                  disabled={readOnly}
                  formatter={handlePercentageChange}
                />
              </Col>
              <Col xs={12} md={6}>
                <TextInput
                  label={t(`${prefix}.activitiesPercentage`)}
                  value={activitiesPercentage}
                  name="activitiesPercentage"
                  control={control}
                  disabled={readOnly || !hasActivities}
                  formatter={handlePercentageChange}
                />
              </Col>
            </Row>
          </>
        )}
      </div>
    </Suspense>
  );
};
